import React, { useEffect, ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';

// Initialize i18n
import './i18n';

// Type for protected route props
interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string;
}

// Redirect component for old case route
const CaseRedirect: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  return <Navigate to={`/lexy/case/${caseId}/workspace`} replace />;
};
import '@/App.css';
import { PlanProvider } from './context/PlanContext';
import { DarkModeProvider } from './context/DarkModeContext';
import { LanguageProvider } from './context/LanguageContext';
import Landing from './pages/LandingWorldClass';
import LandingLinear from './pages/LandingLinear';
import LandingLinear2 from './pages/LandingLinear2';
import BillingSettings from './pages/settings/BillingSettings';
import Venteliste from './pages/Venteliste';
import MetaAd from './pages/MetaAd';
import LogoDownload from './pages/LogoDownload';
import LawyerApply from './pages/LawyerApply';
import LexyDemo from './pages/LexyDemo';
import LexyWorkspace from './pages/LexyWorkspace';
import LexyLanding from './pages/LexyLandingDark';
import LexyLandingWhite from './pages/LexyLandingWhite';
import Lexy2Landing from './pages/Lexy2Landing';
import LexyPresentation from './pages/LexyPresentation';
import InvestorPresentation from './pages/InvestorPresentation';
import LexySignupPage from './pages/LexySignupPage';
import LexyLoginPage from './pages/LexyLoginPage';
import Mandatory2FASetup from './pages/Mandatory2FASetup';
import LexyCommandCenter from './pages/LexyCommandCenter';
import TeamDashboard from './pages/TeamDashboard';
import TeamCapacity from './components/TeamCapacity';
import CapacitySettings from './components/CapacitySettings';
import AINextStepsPage from './pages/AINextStepsPage';
import AIWorkspacePage from './pages/AIWorkspacePage';
import CaseWorkspace from './pages/CaseWorkspaceWorldClass';
import CreateClient from './pages/CreateClient';
import ClientDetailPage from './pages/ClientDetailPage';
import CreateCase from './pages/CreateCase';
import Business from './pages/Business';
import ChatPage from './pages/ChatPage';
import Cases from './pages/Cases';
import CasesWithTabs from './pages/CasesWithTabs';
import CaseDetail from './pages/CaseDetail';
import AdminPanel from './pages/AdminPanel';
import AdminQuickLogin from './pages/AdminQuickLogin';
import AuditView from './pages/AuditView';
import ExpertProfile from './pages/ExpertProfile';
import InvoicesPage from './pages/InvoicesPageNew';
import TenantAdminPage from './pages/TenantAdminPage';
import SplitInboxView from './components/inbox/SplitInboxView';
import AFAProfileView from './components/afa/AFAProfileView';
import AFAWorkspaceView from './components/afa/AFAWorkspaceView';
import EarlyAccessWaitlistPremium from './pages/EarlyAccessWaitlistPremium';
import AdCreator from './pages/AdCreator';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import MarketplaceWorkspace from './pages/MarketplaceWorkspace';
import OfferView from './pages/OfferView';
import WordAddinApp from './components/word-addin/WordAddinApp';
import BackgroundPage from './pages/BackgroundPage';
import { PortalLogin, PortalDashboard, PortalCaseDetail } from './pages/portal';
import { 
  AdminLayout, 
  AdminDashboard, 
  AdminInvitations, 
  AdminTenants, 
  AdminStats 
} from './pages/admin';
import BetaRegister from './pages/BetaRegister';
import BetaAuthCallback from './pages/BetaAuthCallback';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TimeTrackingProvider } from './contexts/TimeTrackingContext';
import TimeTrackingIndicator from './components/time-tracking/TimeTrackingIndicator';
import DevAuth from './components/DevAuth';
import { Toaster } from './components/ui/sonner';
import { auth } from './utils/auth';
import IntroSlidesWrapper from './components/IntroSlidesWrapper';

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  // Also check localStorage directly as fallback for OAuth scenarios
  const storedUser = React.useMemo(() => {
    try {
      const userData = localStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  }, []);
  
  const storedToken = localStorage.getItem('token');
  
  // Use either context user or stored user
  const effectiveUser = user || storedUser;
  const effectiveAuth = isAuthenticated || (!!storedToken && !!storedUser);
  
  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Laster...</p>
        </div>
      </div>
    );
  }
  
  if (!effectiveAuth) {
    return <Navigate to="/" replace />;
  }
  
  if (requiredRole) {
    // Allow ADMIN to access everything
    if (effectiveUser?.role === 'ADMIN') {
      return <>{children}</>;
    }
    // For TENANT_ADMIN, also allow LAWYER (firm members can view org settings)
    if (requiredRole === 'TENANT_ADMIN' && (effectiveUser?.role === 'TENANT_ADMIN' || effectiveUser?.role === 'LAWYER')) {
      return <>{children}</>;
    }
    // Otherwise check exact role match
    if (effectiveUser?.role !== requiredRole) {
      return <Navigate to="/" replace />;
    }
  }
  
  return <>{children}</>;
};

// Wrapper component for app content
function AppContent(): React.ReactElement {
  const navigate = useNavigate();

  // Listen for auth:unauthorized event from axios interceptor
  useEffect(() => {
    const handleUnauthorized = () => {
      // Don't redirect if already on a public page
      const publicPaths = ['/', '/en', '/lexy', '/lexy/landing', '/advokatforalle', '/lexy/login', '/lexy/signup', '/lexy/pricing', '/venteliste', '/business', '/privacy', '/terms', '/lexy-dark'];
      const currentPath = window.location.pathname;
      
      if (publicPaths.includes(currentPath)) {
        console.log('🔓 On public page, not redirecting after 401');
        return;
      }
      
      // Redirect to landing page without full page reload
      navigate('/');
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [navigate]);

  console.log("AppContent rendering - routes available", window.location.pathname);
  
  return (
    <IntroSlidesWrapper>
      <Routes>
        {/* Public pages - Norwegian (default) */}
        <Route path="/" element={<LandingLinear2 lang="nb" />} />
        <Route path="/old" element={<LandingLinear lang="nb" />} />
        {/* Public pages - English */}
        <Route path="/en" element={<LandingLinear2 lang="en" />} />
        <Route path="/lexy-dark" element={<LexyLanding />} />
        <Route path="/advokatforalle" element={<Landing />} />
        <Route path="/venteliste" element={<Venteliste />} />
        <Route path="/business" element={<Business />} />
        <Route path="/advokat/apply" element={<LawyerApply />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/register" element={<BetaRegister />} />
        <Route path="/beta-auth-callback" element={<BetaAuthCallback />} />
        
        {/* Lexy Standalone Product */}
        <Route path="/lexy" element={<LexyLandingWhite />} />
        <Route path="/lexy/landing" element={<LexyLandingWhite />} />
        <Route path="/lexy2" element={<Lexy2Landing />} />
        <Route path="/lexy/presentation" element={<LexyPresentation />} />
        <Route path="/Presentation" element={<LexyPresentation />} />
        <Route path="/investor-presentation" element={<InvestorPresentation />} />
        <Route path="/lexy/early-access" element={<EarlyAccessWaitlistPremium />} />
        <Route path="/lexy/ad" element={<AdCreator />} />
        <Route path="/lexy/signup" element={<LexySignupPage />} />
        <Route path="/lexy/login" element={<LexyLoginPage lang="nb" />} />
        <Route path="/en/lexy/login" element={<LexyLoginPage lang="en" />} />
        <Route path="/lexy/setup-2fa" element={<Mandatory2FASetup />} />
        <Route path="/lexy/clients/new" element={<CreateClient />} />
        <Route path="/lexy/clients/:clientId" element={<ClientDetailPage />} />
        <Route path="/lexy/case/new" element={<CreateCase />} />
        {/* Redirect old case route to workspace (CaseWorkspace is the modern design) */}
        <Route path="/lexy/case/:caseId" element={<CaseRedirect />} />
        <Route path="/lexy/case/:caseId/workspace" element={<CaseWorkspace />} />
        <Route path="/lexy/afa/case/:caseId" element={<AFAWorkspaceView />} />
        <Route path="/lexy/dashboard" element={<LexyCommandCenter />} />
        <Route path="/lexy/ai-workspace" element={<AIWorkspacePage />} />
        <Route path="/lexy/ai-workspace/:caseId" element={<AIWorkspacePage />} />
        <Route path="/lexy/ai-workspace/pre-case/:threadId" element={<AIWorkspacePage />} />
        <Route path="/lexy/afa/profile" element={<AFAProfileView />} />
        <Route path="/lexy/cases" element={<CasesWithTabs />} />
        <Route path="/lexy/team" element={<TeamDashboard />} />
        <Route path="/lexy/capacity" element={<TeamCapacity />} />
        <Route path="/lexy/settings/capacity" element={<CapacitySettings />} />
        <Route path="/lexy/ai-next-steps" element={<AINextStepsPage />} />
        <Route path="/lexy/inbox" element={<ProtectedRoute><SplitInboxView /></ProtectedRoute>} />
        <Route path="/lexy/expert/profile" element={<ExpertProfile />} />
        <Route path="/lexy/invoices" element={<InvoicesPage />} />
        <Route path="/lexy/pricing" element={<LexyLandingWhite />} />
        <Route path="/lexy/demo" element={<DevAuth><LexyDemo /></DevAuth>} />
        
        {/* Client Portal Routes */}
        <Route path="/portal/login" element={<PortalLogin />} />
        <Route path="/portal/dashboard" element={<PortalDashboard />} />
        <Route path="/portal/case/:caseId" element={<PortalCaseDetail />} />
        
        {/* Word Add-in Route */}
        <Route path="/word-addin" element={<WordAddinApp />} />
        <Route path="/word-addin/*" element={<WordAddinApp />} />
        
        {/* Background Only Page */}
        <Route path="/background" element={<BackgroundPage />} />
        
        {/* Payment Routes */}
        <Route path="/success" element={<PaymentSuccess />} />
        <Route path="/cancel" element={<PaymentCancel />} />
        
        {/* Consumer Product */}
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/cases" element={<Cases />} />
        <Route path="/case/:id" element={<CaseDetail />} />
        <Route path="/offer/:offerId" element={<OfferView />} />
        <Route path="/marketplace/case/:id" element={<MarketplaceWorkspace />} />
        <Route path="/admin/tenant" element={
          <ProtectedRoute requiredRole="TENANT_ADMIN">
            <TenantAdminPage />
          </ProtectedRoute>
        } />
        
        {/* New Lexy Super Admin Panel */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="invitations" element={<AdminInvitations />} />
          <Route path="tenants" element={<AdminTenants />} />
          <Route path="stats" element={<AdminStats />} />
        </Route>
        
        <Route path="/admin/quick-login" element={<AdminQuickLogin />} />
        <Route path="/audit" element={<AuditView />} />
        
        {/* Settings Routes */}
        <Route path="/settings/billing" element={<BillingSettings />} />
      </Routes>
    </IntroSlidesWrapper>
  );
}

function App(): React.ReactElement {
  // Check if we're in the Word add-in context - render without router
  const isWordAddin = window.location.pathname.startsWith('/word-addin');
  
  if (isWordAddin) {
    return (
      <ErrorBoundary>
        <LanguageProvider>
          <div className="App">
            <WordAddinApp />
            <Toaster position="top-right" />
          </div>
        </LanguageProvider>
      </ErrorBoundary>
    );
  }
  
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <TimeTrackingProvider>
            <DarkModeProvider>
              <PlanProvider>
                <div className="App">
                  <BrowserRouter>
                    <AppContent />
                    <TimeTrackingIndicator />
                  </BrowserRouter>
                  <Toaster position="top-right" />
                </div>
              </PlanProvider>
            </DarkModeProvider>
          </TimeTrackingProvider>
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;